/**
 * ── ⭐ THE BAKE-STATE MODEL (owner direction #4) ────────────────────────────────────────────────
 *
 * *"We need to design a workflow, much like any other rendering system where there is a state
 * where it's baked or not baked."*
 *
 * Five states, no more, and they answer ONE question: **is the lighting this session is showing
 * you current for this world?** That is a different question from the one `GiInfo.state` answers
 * (*"did the bake function run"*), which is why this is a separate field rather than three more
 * values on that one — conflating "the bake attempt" with "the world's lighting" is how a build
 * step comes to report success about something it never checked.
 *
 * | state | means | how it arises |
 * |---|---|---|
 * | `unbaked` | this world has **no** baked lighting on disk | no `gi/probes.json`, or a 404 |
 * | `baking` | a bake is running **right now** | set BEFORE the bake starts and surviving it — the bake is one long synchronous JS turn, so without this "the trigger never fired" and "the bake is still running" are the same observation (that ambiguity already cost a 15-minute test timeout) |
 * | `baked` | a volume is bound and it passed every provenance check | `checkProvenance` returned OK, or this session baked one |
 * | `stale` | a file **exists** and was **REFUSED** | `checkProvenance` returned a reason |
 * | `failed` | a bake ran and threw | `ProbeBudgetError`, a GPU error, a readback that returned null |
 *
 * ── ⭐ `stale` IS THE STATE THAT DOES THE WORK, AND IT IS THE ONE THAT DID NOT EXIST ────────────
 *
 * Before this, a refused file collapsed into `source: 'none'` — indistinguishable from a world
 * that was never baked. So an artist looking at a flat, wrong-looking level could not tell *"this
 * level has no baked lighting yet"* from *"this level's baked lighting is for a version of the
 * level that no longer exists"*. Those two demand completely different actions, and that is the
 * whole reason UE and Unity surface a lighting state at all.
 *
 * ── WHY THIS IS ITS OWN MODULE ──────────────────────────────────────────────────────────────────
 *
 * Because `src/presentation/scene.ts` is EAGER and every `render/gi/**` module it touches is
 * dynamic-imported. `gi()` is synchronous, so the derivation has to be reachable without an
 * `await` — and a static import of `probeFile.ts` (the natural home, next to `checkProvenance`)
 * would pull the whole file format, its encoder and its parser into the main chunk for five lines
 * of `if`. So the five lines live alone, dependency-free, statically imported by the one caller,
 * and pure enough to sit in `tests/purity.test.ts`'s `PURE_FILES`.
 *
 * ── WHAT MAKES A BAKE STALE — the hash of exactly what ──────────────────────────────────────────
 *
 * `probeFile.checkProvenance` is the single authority and its table is the answer: format, baker
 * version, worldId, **`worldHash`** (the parsed, schema-defaulted `world.json` — placements,
 * prefabs, the asset manifest and the whole `lighting` block including `gi`), payload length, the
 * build's brick constants, the authored `volumes` (sorted by id), the origin, the resolved sun,
 * the resolved sky, the ambient fill, and the geometry-drift smoke alarm. It is REUSED rather
 * than reproduced: this module decides how a verdict is NAMED, never what the verdict is. A
 * parallel staleness mechanism would be a second answer to the one question that file exists to
 * answer.
 *
 * Two things deliberately NOT in that hash, stated so nobody adds them: any **timestamp** (a bake
 * must be byte-reproducible, and a timestamp makes every re-bake look like a change), and the
 * **quality preset** (a preview bake is a valid atlas of the same world — reported, never
 * refused; see `bakeQuality.ts`).
 *
 * ── TWO LIMITS, STATED RATHER THAN DISCOVERED (the dev badge's tooltip says both) ───────────────
 *
 * 1. **A mesh edited in place is not detected.** `worldHash` names a `.glb` by URI; its CONTENTS
 *    are outside every hash here, and `geometryDrift` is a smoke alarm, not a checksum — a wall
 *    re-textured or a doorway widened inside the same bounding box passes. Closing it properly
 *    means hashing the cooked asset bytes, which makes every `cook:world` invalidate every bake;
 *    UE and Unity effectively accept the same limit. **Re-bake after changing geometry.**
 * 2. **Staleness is checked at LOAD, not continuously.** Editing the world in the editor
 *    mid-session does not flip the state until reload. Say it; do not build a watcher.
 *    ⚠️ **L2 ADDED EXACTLY ONE MORE MOMENT, AND IT IS NOT A WATCHER.**
 *    `GameScene.reloadBakedLighting()` re-reads the file once, when a caller KNOWS it moved — the
 *    bake panel calls it for a bake this tab started, for this world, that has just reported
 *    `done`. It runs `checkProvenance` itself, on the same live inputs the boot load uses, so it
 *    is a second CALL of the one authority and not a second authority. Nothing polls the disk,
 *    nothing subscribes to it, and an edit made in the editor still does not flip anything.
 */

/** The five states. Five, and no more — a sixth would be a state nobody could act on. */
export type BakeState = 'unbaked' | 'baking' | 'baked' | 'stale' | 'failed';

/** The three facts the state is derived FROM — all of them already published in `GiInfo`. */
export interface BakeStateInputs {
  /** Did the bake function run, and how did it end? */
  state: 'baking' | 'baked' | 'failed';
  /**
   * Where the bound atlas came from. Only `'bake'` matters to this function, and it matters for
   * one case: under `?gi=probe` a stale file is refused and then baked OVER, so `stale` is
   * non-null on a session whose failure was the BAKE's. Without this field that reads as `stale`
   * and tells the artist to re-bake the thing that just failed to bake.
   */
  source: 'bake' | 'file' | 'none' | 'spike';
  /** Why a baked FILE was refused, or null. `'absent'` is the ordinary no-file case, not a fault. */
  stale: { reason: string } | null;
}

/**
 * ⭐ ONE DERIVATION, DERIVED AND NEVER STORED. `GameScene.gi()` computes this on the way out
 * rather than each of the eight `giInfo` assignments writing it, because a state remembered at
 * eight sites is a state that will be wrong at one of them. Pure, so the whole truth table is
 * asserted in `npm run check` rather than in a browser.
 */
export function deriveBakeState(info: BakeStateInputs): BakeState {
  // A bake in flight outranks everything: whatever is (or is not) on disk, THIS is what is happening.
  if (info.state === 'baking') return 'baking';
  /*
   * A volume is bound and current. Under `?gi=probe` a stale file may ALSO be sitting on disk — it
   * was refused and then baked over, so the lighting in force IS current and the state is `baked`.
   * `GiInfo.stale` still names the field that refused the file; the two facts stay separate.
   */
  if (info.state === 'baked') return 'baked';
  /*
   * `state: 'failed'` splits three ways, and THE SPLIT IS THE DELIVERABLE — before it, all three
   * read as `source: 'none'`:
   *
   *   - a file was fetched and REFUSED       ⇒ stale   (the artist must re-bake)
   *   - no file was there at all (`absent`)  ⇒ unbaked (the artist must bake for the first time)
   *   - no file was involved: a bake threw   ⇒ failed  (the artist must read the error)
   */
  // `'dense'` was a fourth arm here and is retired with the v1 lattice (F3). It joined `'bake'`
  // for the same reason: a session whose own bake failed must not be told to re-bake a file.
  if (info.source === 'bake' || info.stale === null) return 'failed';
  return info.stale.reason === 'absent' ? 'unbaked' : 'stale';
}

/**
 * The word the dev badge shows. Uppercase, and never signalled by colour alone (a11y floor).
 *
 * ── ⚠️ `stale` IS RENDERED `OUT OF DATE`, AND ONLY RENDERED (U2) ────────────────────────────────
 *
 * The enum value stays `stale` — here, in `checkProvenance`, in `GiInfo`, in every test and in
 * `data-bake-state` — because it is the identifier every other surface keys on, and a language pass
 * that renamed a state would not be a language pass. What moves is the two words on the badge.
 *
 * WHY: `STALE` is a CACHE word. It names a property of the tool ("this artefact is out of step with
 * its source") and reads to an artist as a warning about the bake system, when the fact is about
 * their level: *the file is for an older version of it, so the room you are looking at is the
 * constant fill.* And it must move HERE rather than only in the panel — the badge is the surface an
 * artist reads without opening anything, and two surfaces saying different words for one state is
 * the disagreement this subsystem's whole history is made of.
 *
 * This is the ONE place either surface gets the word, so they cannot drift.
 */
export function bakeStateLabel(state: BakeState): string {
  if (state === 'baking') return 'BAKING…';
  if (state === 'stale') return 'OUT OF DATE';
  return state.toUpperCase();
}
